/**
 * The mission's own devices, on the map, as they move.
 *
 * Rendered as markers rather than a GL symbol layer, which is the opposite of
 * the choice COP makes. COP draws a whole fleet and needs the GPU; a mission is
 * a handful of assigned devices, and markers buy per-pin DOM — a heading wedge,
 * a freshness pulse, a hoverable readout — for a cost that does not signify at
 * this count.
 *
 * Everything about how a pin looks is in `deviceMarker.ts`; this file only
 * places them.
 */
import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Marker, Popup } from 'react-map-gl';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FlightIcon from '@mui/icons-material/Flight';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import VideocamIcon from '@mui/icons-material/Videocam';
import WatchIcon from '@mui/icons-material/Watch';
import LiveStreamPlayer from '@signals/cop-ui/components/LiveStreamPlayer';
import {
  DARK_MAP_POPUP_CLASS,
  DarkMapPopupRow,
  DarkMapPopupShell,
  useDarkMapPopupChrome,
} from '../../../components/map/DarkMapPopup';
import { RichTooltip } from '../../../components/common';
import { MissionDeviceKind, MissionDeviceTrack } from '../useMissionDeviceTracks';
import { formatAgeMinutes, formatSpeed } from '../format';
import { useMapIconLabels, useMapLayerVisible } from '../../map/mapLayerVisibility';
import {
  trackColor,
  trackIsFresh,
  trackIsMoving,
  trackName,
  trackOpacity,
  trackStateText,
} from './deviceMarker';
import { pinLabelSide, pinLabelSx } from './pinLabels';

const KIND_ICON: Record<MissionDeviceKind, React.ReactElement> = {
  phone: <PhoneIphoneIcon sx={{ fontSize: 13 }} />,
  watch: <WatchIcon sx={{ fontSize: 13 }} />,
  vehicle: <DirectionsCarIcon sx={{ fontSize: 13 }} />,
  drone: <FlightIcon sx={{ fontSize: 13 }} />,
  wardriver: <SettingsInputAntennaIcon sx={{ fontSize: 13 }} />,
};

export interface MissionDeviceLayerProps {
  tracks: MissionDeviceTrack[];
  /** Centre the host map on a device, usually from a click. */
  onSelect?: (track: MissionDeviceTrack) => void;
  /** Off while a tool owns the map, so a pin cannot swallow a placement click. */
  interactive?: boolean;
  units?: 'km' | 'mi';
}

const MissionDeviceLayer: React.FC<MissionDeviceLayerProps> = ({
  tracks,
  onSelect,
  interactive = true,
  units = 'mi',
}) => {
  const [watching, setWatching] = useState<string | null>(null);
  const live = tracks.find((t) => t.deviceId === watching && t.isStreaming);
  useDarkMapPopupChrome(!!live);
  const visible = useMapLayerVisible('roster');
  const showIconLabels = useMapIconLabels();
  if (!visible) return null;

  const labelItems = tracks.map((t) => ({
    id: t.deviceId,
    latitude: t.latitude,
    longitude: t.longitude,
  }));

  return (
  <>
    {tracks.map((track) => {
      const color = trackColor(track);
      const labelSide = showIconLabels
        ? pinLabelSide(labelItems, track.deviceId, 'left')
        : 'hidden';
      const clickable = interactive && (!!onSelect || track.isStreaming);
      // Everything that is true of the fix rather than of the device: whether
      // it can hear anything, how fast it was going, how old the claim is.
      const provenance = [
        track.isObserver ? 'collecting RF' : null,
        track.speedMps != null ? formatSpeed(track.speedMps, units) : null,
        track.at
          ? `${formatAgeMinutes(track.ageMinutes) || 'unknown age'}${track.source === 'monitor' ? ' (last known)' : ''}`
          : 'no fix',
      ].filter(Boolean).join(' · ');

      return (
        <Marker
          key={track.deviceId}
          longitude={track.longitude}
          latitude={track.latitude}
          anchor="center"
          style={{
            zIndex: 2,
            opacity: trackOpacity(track.ageMinutes),
            pointerEvents: interactive ? 'auto' : 'none',
          }}
        >
          {/* The pin says kind by its icon and state by its colour; the
              readout keeps that pairing, so the eyebrow names the kind and
              takes the same colour the pin is already wearing. */}
          <RichTooltip
            placement="top"
            eyebrow={track.isStationary ? `${track.kind} · fixed site` : track.kind}
            eyebrowTone={color}
            body={(
              <>
                <Box component="strong" sx={{ fontWeight: 700 }}>{trackName(track)}</Box>
                {' — '}
                {trackStateText(track)}
              </>
            )}
            hint={track.isStreaming ? `${provenance} · LIVE video` : provenance}
          >
            <Box
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : -1}
              aria-label={`${track.label}: ${trackStateText(track)}`}
              onClick={clickable ? () => {
                onSelect?.(track);
                if (track.isStreaming) setWatching(track.deviceId);
              } : undefined}
              onKeyDown={(e) => {
                if (!clickable) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect?.(track);
                  if (track.isStreaming) setWatching(track.deviceId);
                }
              }}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 26,
                height: 26,
                cursor: clickable ? 'pointer' : 'default',
              }}
            >
              {trackIsFresh(track) && (
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    border: `2px solid ${color}`,
                    pointerEvents: 'none',
                    '@keyframes missionDevicePulse': {
                      '0%': { transform: 'scale(0.65)', opacity: 0.7 },
                      '75%': { transform: 'scale(2)', opacity: 0 },
                      '100%': { transform: 'scale(2)', opacity: 0 },
                    },
                    animation: 'missionDevicePulse 2.4s ease-out infinite',
                  }}
                />
              )}

              {trackIsMoving(track) && (
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    transform: `rotate(${track.heading}deg)`,
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -7,
                      left: '50%',
                      ml: '-4px',
                      width: 0,
                      height: 0,
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderBottom: `7px solid ${color}`,
                      filter: 'drop-shadow(0 0 2px rgba(11,16,23,0.9))',
                    }}
                  />
                </Box>
              )}

              <Box
                sx={{
                  width: 20,
                  height: 20,
                  // Square for a fixed site, round for a mover: tellable at a
                  // glance even when colour is ambiguous on satellite imagery.
                  borderRadius: track.isStationary ? '4px' : '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(11,16,23,0.92)',
                  color,
                  border: `2px solid ${color}`,
                  boxShadow: '0 1px 5px rgba(0,0,0,0.6)',
                  transition: 'transform 120ms ease',
                  '&:hover': { transform: clickable ? 'scale(1.15)' : 'none' },
                }}
              >
                {KIND_ICON[track.kind] || KIND_ICON.phone}
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  top: 26,
                  px: 0.6,
                  borderRadius: 0.75,
                  bgcolor: 'rgba(11,16,23,0.82)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  fontSize: '0.58rem',
                  fontWeight: 700,
                  lineHeight: 1.6,
                  color: '#e8eef7',
                  ...pinLabelSx(labelSide),
                }}
              >
                {trackName(track)}
              </Box>
              {track.isStreaming && (
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    px: 0.4,
                    borderRadius: 0.5,
                    bgcolor: '#c62828',
                    color: '#fff',
                    fontSize: '0.5rem',
                    fontWeight: 800,
                    letterSpacing: 0.3,
                    lineHeight: 1.5,
                    boxShadow: '0 0 6px #c62828',
                  }}
                >
                  <VideocamIcon sx={{ fontSize: 9 }} />
                  LIVE
                </Box>
              )}
            </Box>
          </RichTooltip>
        </Marker>
      );
    })}
    {live && (
      <Popup
        longitude={live.longitude}
        latitude={live.latitude}
        anchor="bottom"
        offset={16}
        onClose={() => setWatching(null)}
        closeButton={false}
        closeOnClick={false}
        className={DARK_MAP_POPUP_CLASS}
        maxWidth="320px"
        style={{ padding: 0, background: 'transparent', boxShadow: 'none' }}
      >
        <DarkMapPopupShell
          accent="#c62828"
          title={live.label}
          badge="LIVE"
          onClose={() => setWatching(null)}
        >
          <DarkMapPopupRow label="Kind">{live.kind}</DarkMapPopupRow>
          <div style={{ marginTop: 8, borderRadius: 4, overflow: 'hidden' }}>
            <LiveStreamPlayer
              deviceId={live.streamDeviceId || live.deviceId}
              variant="inline"
              label={live.label}
              onClose={() => setWatching(null)}
            />
          </div>
        </DarkMapPopupShell>
      </Popup>
    )}
  </>
  );
};

export default MissionDeviceLayer;
